import { useCallback } from 'react';

import { IS_VC_UNIT_CARD_DETAILS_VISIBLE, VALUE_CHAIN_MAP } from '../constants/general';
import {
  BUSINESS_UNIT_TYPES,
  CONNECTION_TYPES,
  VALUE_CHAIN_CONNECTION_TYPES,
  VALUE_CHAIN_UNIT_TYPES,
} from '../constants/interfaces';
import { getUnitBusinessUnitType, getUnitEstimated, getUnitHeight } from '../utils/reactFlowHelpers';

import type { Edge, Node } from '@xyflow/react';
import type { ID_VALUE } from '../constants/dataSets';
import type IProcessConnections from '../pages/CompanyAdminPages/CompanyMap/components/ValueChain/interfaces/IProcessConnections';
import type { IValueChainBoard } from '../store/slices/valueChainMap/interfaces/IValueChainBoard';
import type { IValueChainBoardColumn } from '../store/slices/valueChainMap/interfaces/IValueChainBoardColumn';
import type { IValueChainUnit } from '../store/slices/valueChainMap/interfaces/IValueChainUnit';
import type { IEdgeGroupMap } from './interfaces/IEdgeGroupMap';
import type { IPushNewBoardNode } from './interfaces/IPushNewBoardNode';
import type { IPushNewEdge } from './interfaces/IPushNewEdge';
import type { IPushNewSwimlaneNode } from './interfaces/IPushNewSwimlaneNode';
import type { IPushNewUnitNode } from './interfaces/IPushNewUnitNode';

const CONNECTION_TYPE_ORDER: readonly string[] = [
  VALUE_CHAIN_CONNECTION_TYPES.LOGICAL,
  VALUE_CHAIN_CONNECTION_TYPES.SERVICE,
  VALUE_CHAIN_CONNECTION_TYPES.SUPPLIER_OF_GOODS,
  VALUE_CHAIN_CONNECTION_TYPES.MANUFACTURE,
] as const;

const processEdgesAndHandles = (
  edges: Edge[],
): { edges: Edge[] } => {
  if (!edges || edges.length === 0) {
    return { edges: [] };
  }

  const sourceGroups: IEdgeGroupMap = {};
  const sourceTargetPairs: IEdgeGroupMap = {};

  edges.forEach((edge) => {
    if (edge.source) {
      sourceGroups[edge.source] = sourceGroups[edge.source] || [];
      sourceGroups[edge.source].push(edge);
    }
    const pairKey = `${edge.source}-${edge.target}`;
    sourceTargetPairs[pairKey] = sourceTargetPairs[pairKey] || [];
    sourceTargetPairs[pairKey].push(edge);
  });

  const calculateOffsetByType = (
    edge: Edge,
    edgesGroup: Edge[],
    typeOrder: readonly string[],
  ): number => {
    const connectionType = (edge.data as { connectionType?: string })?.connectionType;

    const byType: Record<string, Edge[]> = {};
    edgesGroup.forEach((e) => {
      const ct = (e.data as { connectionType?: string })?.connectionType;
      if (ct) {
        byType[ct] = byType[ct] || [];
        byType[ct].push(e);
      }
    });

    const presentTypes = typeOrder.filter((t) => byType[t]?.length > 0);

    if (presentTypes.length === 1) {
      return 0;
    }

    // Keep arrowheads tighter to the center of the card
    // so that, when cards are collapsed, markers do not
    // stick out beyond the card height.
    const gapBetweenTypes = 8;
    const maxOffsetFromCenter = 18;

    const typePositions: Record<string, number> = {};

    if (presentTypes.length === 2) {
      typePositions[presentTypes[0]] = -gapBetweenTypes / 2;
      typePositions[presentTypes[1]] = gapBetweenTypes / 2;
    } else if (presentTypes.length === 3) {
      typePositions[presentTypes[0]] = -gapBetweenTypes;
      typePositions[presentTypes[1]] = 0;
      typePositions[presentTypes[2]] = gapBetweenTypes;
    } else {
      const totalGaps = (presentTypes.length - 1) * gapBetweenTypes;
      const availableHeight = Math.min(totalGaps, maxOffsetFromCenter * 2);
      const startOffset = -availableHeight / 2;
      let currentOffset = startOffset;

      presentTypes.forEach((t, index) => {
        typePositions[t] = currentOffset;
        if (index < presentTypes.length - 1) {
          currentOffset += gapBetweenTypes;
        }
      });
    }

    return typePositions[connectionType || ''] || 0;
  };

  const resultEdges = edges.map((edge, edgeIndex) => {
    let sourceOffset = 0;
    let targetOffset = 0;
    let pathOffset = 0;

    if (edge.source && sourceGroups[edge.source].length > 1) {
      const sourceGroup = sourceGroups[edge.source];
      sourceOffset = calculateOffsetByType(edge, sourceGroup, CONNECTION_TYPE_ORDER);
    }

    const pairKey = `${edge.source}-${edge.target}`;
    if (sourceTargetPairs[pairKey].length > 1) {
      const pairGroup = sourceTargetPairs[pairKey];
      const baseOffset = calculateOffsetByType(edge, pairGroup, CONNECTION_TYPE_ORDER);
      pathOffset = baseOffset * 1.5;
    } else {
      pathOffset = ((edgeIndex % 7) - 3) * 5;
    }

    if (edge.target) {
      const targetGroup = edges.filter((e) => e.target === edge.target);
      if (targetGroup.length > 1) {
        targetOffset = calculateOffsetByType(edge, targetGroup, CONNECTION_TYPE_ORDER);
      }
    }

    const out: Edge = {
      ...edge,
      data: {
        ...edge.data,
        sourceOffset,
        targetOffset,
        pathOffset,
      },
    };
    return out;
  });

  return { edges: resultEdges };
};

// Target 24px visual gap between cards for both
// logical and physical units to match UI expectations.
const getSiblingPadding = (): number => VALUE_CHAIN_MAP.UNIT_VERTICAL_GAP;

const getCardLevelFromDepth = (depth: number): number => depth + 1;

const NON_CANVAS_UNIT_TYPES = new Set([
  VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER,
  VALUE_CHAIN_UNIT_TYPES.OWN_OPERATION,
]);

const filterOutProfitCenterUnits = (units: IValueChainUnit[]): IValueChainUnit[] => (
  units.filter((unit) => !NON_CANVAS_UNIT_TYPES.has(unit.type))
);

const shouldCardBeCollapsed = (
  level: number,
  hasEntities: boolean,
  siblingsAtSameLevel: number,
): boolean => {
  const hasNoEntities = !hasEntities;
  const hasManySiblings = siblingsAtSameLevel >= 6;

  const isLevelBetween = (from: number, to: number) => level >= from && level <= to;

  if (isLevelBetween(2, 5) && hasNoEntities && hasManySiblings) {
    return true;
  }

  if (isLevelBetween(3, 5) && hasNoEntities) {
    return true;
  }

  if (isLevelBetween(3, 5) && hasEntities && hasManySiblings) {
    return true;
  }

  return false;
};

const getVisualUnitHeightWithChildren = (
  unit: IValueChainUnit,
  totalChildrenHeight: number,
): number => {
  const estimated = getUnitEstimated(unit);
  const baseHeight = getUnitHeight(unit.type, getUnitBusinessUnitType(unit), estimated);

  if (!totalChildrenHeight || totalChildrenHeight <= 0) {
    return baseHeight;
  }

  // Must match CustomUnitContainer formula: gap + children height + container margin so layout aligns
  return baseHeight
    + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING
    + totalChildrenHeight
    + VALUE_CHAIN_MAP.UNIT_CONTAINER_MARGIN_BOTTOM;
};

const getCollapsedUnitHeight = (unit: IValueChainUnit): number => {
  const hasLogicalStatus = getUnitBusinessUnitType(unit) === BUSINESS_UNIT_TYPES.LOGICAL
    || Boolean(getUnitEstimated(unit));

  const baseHeaderHeight = VALUE_CHAIN_MAP.CORRECTED_PROFIT_CENTER_HEIGHT;
  const statusHeight = hasLogicalStatus ? VALUE_CHAIN_MAP.LOGICAL_STATUS_HEIGHT : 0;

  return baseHeaderHeight + statusHeight;
};

const useCreateNodesAndEdgesValueChain = (
  onEditSwimlane: (board: IValueChainBoardColumn) => void,
  onDeleteSwimlane: (board: IValueChainBoardColumn) => void,
  isAccessDisabled: boolean,
) => {
  const createNodesAndEdges = useCallback((
    dataT: IValueChainBoard[],
    prevNodes?: Node[],
    isDraftReviewMode: boolean = false,
  ): { nodes: Node[], edges: Edge[], accumulatedMaxBoardHeight: number } => {
    const nodesT: Node[] = [];
    const edgesT: Edge[] = [];
    let nextAccumulatedMaxBoardHeight = VALUE_CHAIN_MAP.TITLE_HEIGHT * 2;

    const prevUnitNodesById = new Map<string, Node>();

    if (prevNodes && prevNodes.length > 0) {
      prevNodes.forEach((prevNode) => {
        if (prevNode.type === 'unit') {
          prevUnitNodesById.set(prevNode.id, prevNode);
        }
      });
    }

    const pushNewEdge = ({
      sourceId,
      connection,
      connectionType,
    }: IPushNewEdge) => {
      const isDraftConnection = Boolean((connection as { draft?: boolean }).draft);

      edgesT.push({
        type: 'custom',
        id: `e-${sourceId}-unit_${connection.toId}-type_${connectionType}`,
        source: sourceId,
        target: `unit_${connection.toId}`,
        data: {
          connection,
          connectionType,
          draft: isDraftConnection,
          isDraftReviewMode,
        },
      });
    };

    const pushNewBoardNode = ({ board, xPosition, boardWidth }: IPushNewBoardNode) => {
      nodesT.push({
        id: `board_${board.id}`,
        type: 'board',
        draggable: false,
        focusable: false,
        selectable: false,
        style: { zIndex: 0 },
        data: {
          ...board,
          boardWidth,
          isDraftReviewMode,
        },
        position: { x: xPosition, y: 0 },
      });
    };

    const pushNewSwimlaneNode = ({
      unit, xPosition, yPosition, boardId,
    }: IPushNewSwimlaneNode) => {
      const isDraftColumn = Boolean((unit as IValueChainBoardColumn & { draft?: boolean }).draft);

      nodesT.push({
        id: `swimlane_${unit.id}`,
        type: 'swimlane',
        draggable: !isAccessDisabled && !isDraftReviewMode,
        dragHandle: '.react-flow__node-drag-handle',
        style: { zIndex: 1 },
        data: {
          ...unit,
          draft: isDraftColumn,
          isDraftReviewMode,
          boardId,
          onEditSwimlane,
          onDeleteSwimlane,
          isAccessDisabled,
        },
        position: { x: xPosition, y: yPosition },
      });
    };

    const pushNewUnitNode = ({
      unit,
      xPosition,
      yPosition,
      childrenCount,
      parentId,
      swimlaneId,
      totalChildrenHeight,
      depth,
      isCollapsed,
      isHiddenByParent,
    }: IPushNewUnitNode) => {
      const isDraftUnit = Boolean((unit as IValueChainUnit & { draft?: boolean }).draft);

      nodesT.push({
        id: `unit_${unit.id}`,
        type: 'unit',
        draggable: !isAccessDisabled && !isDraftReviewMode,
        connectable: !isDraftReviewMode,
        selectable: !isDraftReviewMode,
        style: { zIndex: isDraftReviewMode && isDraftUnit ? 40 : 1 },
        data: {
          ...unit,
          draft: isDraftUnit,
          isDraftReviewMode,
          childrenCount,
          swimlaneId: `swimlane_${swimlaneId}`,
          totalChildrenHeight,
          depth,
          isCollapsed,
          isHiddenByParent,
        },
        position: { x: xPosition, y: yPosition },
        parentId,
        // Don't use extent constraint to allow dragging out of parent
        extent: undefined,
      });
    };

    const processConnections = (
      sourceUnit: Pick<IValueChainUnit, typeof ID_VALUE>,
      connections: IProcessConnections,
    ) => {
      Object.entries(CONNECTION_TYPES)
        .forEach(([key, connectionType]) => {
          const unitConnections = connections[key as keyof IProcessConnections];
          if (unitConnections?.length) {
            unitConnections.forEach((connection) => {
              pushNewEdge({
                sourceId: `unit_${sourceUnit.id}`,
                connection,
                connectionType,
              });
            });
          }
        });
    };

    let accumulatedBoardXPosition = 0;

    // Iterate over each board to create nodes and edges
    dataT.forEach((board) => {
      let additionalSpace = 0;
      if (board.columns.length > 1) {
        additionalSpace = (board.columns.length - 1) * VALUE_CHAIN_MAP.SWIMLANE_GAP;
      }
      const boardWidth = VALUE_CHAIN_MAP.BOARD_PADDING * 2 + board.columns.length * VALUE_CHAIN_MAP.SWIMLANE_WIDTH
        + additionalSpace;

      const boardStartX = accumulatedBoardXPosition;

      // Track maximum content height for this specific board.
      // This will be used to set the board height, with an additional safety margin.
      let boardMaxContentHeight = VALUE_CHAIN_MAP.TITLE_HEIGHT * 2;

      pushNewBoardNode({
        board,
        xPosition: boardStartX,
        boardWidth,
      });

      accumulatedBoardXPosition += boardWidth + VALUE_CHAIN_MAP.BOARD_GAP;

      let accumulatedSwimlaneXPosition = VALUE_CHAIN_MAP.BOARD_PADDING;

      // Sort columns by orderIndex before iterating
      const sortedColumns = [...board.columns].sort((a, b) => a.orderIndex - b.orderIndex);

      // Iterate over each column in the board
      sortedColumns.forEach((column, columnIndex) => {
        pushNewSwimlaneNode({
          unit: column,
          xPosition: boardStartX + accumulatedSwimlaneXPosition,
          yPosition: VALUE_CHAIN_MAP.TITLE_HEIGHT,
          boardId: board.id,
        });

        let accumulatedUnitParentYPosition = VALUE_CHAIN_MAP.TITLE_HEIGHT * 2 + VALUE_CHAIN_MAP.UNIT_TOP_PADDING;

        // Sort units by orderIndex before iterating
        const sortedUnits = filterOutProfitCenterUnits(column.units)
          .sort((a, b) => a.orderIndex - b.orderIndex);
        const rootSiblingsCount = sortedUnits.length;

        // Recursive function to process connections for all nesting levels
        const processUnitAndChildrenConnections = (unit: IValueChainUnit) => {
          // Process unit connections
          processConnections(unit, {
            lConToUnits: unit.lConToUnits,
            sConToUnits: unit.sConToUnits,
            gsConToUnits: unit.gsConToUnits,
            mConToUnits: unit.mConToUnits,
          });

          // Recursively process children connections
          if (unit.children && unit.children.length > 0) {
            const sortedChildren = filterOutProfitCenterUnits(unit.children)
              .sort((a, b) => a.orderIndex - b.orderIndex);
            sortedChildren.forEach((childUnit) => {
              processUnitAndChildrenConnections(childUnit);
            });
          }
        };

        // Recursive function to calculate total height of all nested children,
        // respecting the current isCollapsed state preserved from prevNodes.
        // When a child is collapsed, only its header height contributes; its own
        // children are not rendered and must not be counted.
        const calculateTotalChildrenHeight = (children: IValueChainUnit[]): number => {
          const filteredChildren = filterOutProfitCenterUnits(children);
          if (filteredChildren.length === 0) return 0;

          let totalHeight = 0;
          const sortedChildren = [...filteredChildren].sort((a, b) => a.orderIndex - b.orderIndex);

          sortedChildren.forEach((child, index) => {
            const prevChildNode = prevUnitNodesById.get(`unit_${child.id}`);
            const isChildCollapsed = Boolean(
              (prevChildNode?.data as { isCollapsed?: boolean } | undefined)?.isCollapsed,
            );

            if (isChildCollapsed) {
              // Collapsed child only shows its header — no need to recurse into its children
              totalHeight += getCollapsedUnitHeight(child);
            } else {
              // Recursively calculate height of nested children
              const nestedChildrenHeight = child.children && child.children.length > 0
                ? calculateTotalChildrenHeight(child.children)
                : 0;

              // Must use the same formula as getVisualUnitHeightWithChildren
              // (and CustomUnitContainer) so the reserved space matches what is rendered
              totalHeight += getVisualUnitHeightWithChildren(child, nestedChildrenHeight);
            }

            // Add padding between siblings (but not after the last child)
            if (index < sortedChildren.length - 1) {
              totalHeight += getSiblingPadding();
            }
          });

          return totalHeight;
        };

        // Recursive function to create child nodes at all nesting levels
        // startYPosition is relative to the parent node (not absolute)
        const createChildNodes = (
          parentUnit: IValueChainUnit,
          parentId: string,
          startYPosition: number,
          parentDepth: number = 0,
          hasCollapsedAncestor: boolean = false,
        ): number => {
          const filteredChildren = filterOutProfitCenterUnits(parentUnit.children);
          if (filteredChildren.length === 0) return startYPosition;

          let currentYPosition = startYPosition;
          const sortedChildren = [...filteredChildren].sort((a, b) => a.orderIndex - b.orderIndex);
          const childDepth = parentDepth + 1;
          const childLevel = getCardLevelFromDepth(childDepth);
          const childrenAtSameLevelCount = sortedChildren.length;

          sortedChildren.forEach((childUnit, index) => {
            // Calculate total height for this child's children
            let childTotalChildrenHeight = 0;
            if (childUnit.children && childUnit.children.length > 0) {
              childTotalChildrenHeight = calculateTotalChildrenHeight(childUnit.children);
            }

            const hasEntities = (childUnit.records?.length || 0) > 0;
            const prevChildNode = prevUnitNodesById.get(`unit_${childUnit.id}`);
            const prevChildIsCollapsed = typeof (
              prevChildNode?.data as { isCollapsed?: boolean } | undefined)?.isCollapsed === 'boolean'
              ? Boolean((prevChildNode?.data as { isCollapsed?: boolean }).isCollapsed)
              : undefined;

            // Auto-collapse only makes sense while cards are expandable (details visible)
            const autoChildCollapsed = IS_VC_UNIT_CARD_DETAILS_VISIBLE && shouldCardBeCollapsed(
              childLevel,
              hasEntities,
              childrenAtSameLevelCount,
            );

            const isChildCollapsed = typeof prevChildIsCollapsed === 'boolean'
              ? prevChildIsCollapsed
              : autoChildCollapsed;

            const childUnitHeight = getUnitHeight(
              childUnit.type,
              getUnitBusinessUnitType(childUnit),
              getUnitEstimated(childUnit),
            );

            // Calculate visual height including nested children (for positioning next sibling)
            const visualChildHeight = isChildCollapsed
              ? getCollapsedUnitHeight(childUnit)
              : getVisualUnitHeightWithChildren(childUnit, childTotalChildrenHeight);

            // Create node for this child
            // Position is relative to parent node (React Flow handles parentId positioning)
            pushNewUnitNode({
              unit: childUnit,
              xPosition: 5,
              yPosition: currentYPosition,
              childrenCount: childUnit.children?.length || 0,
              parentId,
              swimlaneId: column.id,
              totalChildrenHeight: childTotalChildrenHeight,
              depth: childDepth,
              isCollapsed: isChildCollapsed,
              isHiddenByParent: hasCollapsedAncestor,
            });

            // Recursively create nested children BEFORE updating position for next sibling
            // Nested children should start right after the child's base height + padding
            if (childUnit.children && childUnit.children.length > 0) {
              const nestedChildrenStartY = childUnitHeight + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING;
              createChildNodes(
                childUnit,
                `unit_${childUnit.id}`,
                nestedChildrenStartY,
                childDepth,
                hasCollapsedAncestor || isChildCollapsed,
              );
            }

            // Move position down by visual height (includes nested children) for next sibling
            currentYPosition += visualChildHeight;

            // Add padding between siblings (but not after the last child)
            if (index < sortedChildren.length - 1) {
              currentYPosition += getSiblingPadding();
            }
          });

          return currentYPosition;
        };

        sortedUnits.forEach((unit) => {
          // Process main unit and all its nested children connections
          processUnitAndChildrenConnections(unit);

          // Calculate total children height before creating parent unit
          let totalChildrenHeight = 0;
          if (unit.children.length > 0) {
            totalChildrenHeight = calculateTotalChildrenHeight(unit.children);
          }

          const rootDepth = 0;
          const rootLevel = getCardLevelFromDepth(rootDepth);
          const hasRootEntities = (unit.records?.length || 0) > 0;
          const prevRootNode = prevUnitNodesById.get(`unit_${unit.id}`);
          const prevRootIsCollapsed = typeof (
            prevRootNode?.data as { isCollapsed?: boolean } | undefined)?.isCollapsed === 'boolean'
            ? Boolean((prevRootNode?.data as { isCollapsed?: boolean }).isCollapsed)
            : undefined;

          const autoRootCollapsed = IS_VC_UNIT_CARD_DETAILS_VISIBLE && shouldCardBeCollapsed(
            rootLevel,
            hasRootEntities,
            rootSiblingsCount,
          );

          const isRootCollapsed = typeof prevRootIsCollapsed === 'boolean'
            ? prevRootIsCollapsed
            : autoRootCollapsed;

          const baseUnitHeight = getUnitHeight(
            unit.type,
            getUnitBusinessUnitType(unit),
            getUnitEstimated(unit),
          );

          const visualUnitFullHeight = getVisualUnitHeightWithChildren(unit, totalChildrenHeight);
          const visualUnitHeight = isRootCollapsed
            ? getCollapsedUnitHeight(unit)
            : visualUnitFullHeight;

          // Add unit nodes - position relative to swimlane parent
          // Units directly in swimlane have depth 0
          pushNewUnitNode({
            unit,
            xPosition: VALUE_CHAIN_MAP.UNIT_HORIZONTAL_PADDING,
            yPosition: accumulatedUnitParentYPosition - VALUE_CHAIN_MAP.TITLE_HEIGHT,
            childrenCount: unit.children.length,
            parentId: `swimlane_${column.id}`,
            swimlaneId: column.id,
            totalChildrenHeight,
            depth: rootDepth,
            isCollapsed: isRootCollapsed,
            isHiddenByParent: false,
          });

          // Create child nodes recursively BEFORE updating accumulatedUnitParentYPosition
          // Position children relative to parent card (after parent BASE height + padding)
          if (unit.children.length > 0) {
            // Start children after the parent card's BASE content height (not visual height with children)
            // Add padding to separate children from parent content
            // Use UNIT_RECORD_PADDING to match the spacing used in the layout
            const childStartYPosition = baseUnitHeight + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING;
            createChildNodes(unit, `unit_${unit.id}`, childStartYPosition, 0, isRootCollapsed);
          }

          accumulatedUnitParentYPosition += visualUnitHeight
          + getSiblingPadding();

          // Update board max content height for root-level units as well
          boardMaxContentHeight = Math.max(
            boardMaxContentHeight,
            accumulatedUnitParentYPosition + VALUE_CHAIN_MAP.PADDING_CONSTANT,
          );
        });
        accumulatedSwimlaneXPosition += VALUE_CHAIN_MAP.SWIMLANE_WIDTH
          + (columnIndex !== sortedColumns.length - 1 ? VALUE_CHAIN_MAP.SWIMLANE_GAP : 0);
      });

      // Keep a single derived height for all board/swimlane nodes in this layout pass.
      nextAccumulatedMaxBoardHeight = Math.max(
        nextAccumulatedMaxBoardHeight,
        boardMaxContentHeight + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING,
      );
    });

    const nodesWithBoardHeight = nodesT.map((node) => {
      if (node.type !== 'board' && node.type !== 'swimlane') {
        return node;
      }

      return {
        ...node,
        data: {
          ...node.data,
          accumulatedMaxBoardHeight: nextAccumulatedMaxBoardHeight,
        },
      };
    });

    const validNodeIds = new Set(
      nodesWithBoardHeight
        .filter((node) => node.type === 'unit')
        .map((node) => node.id),
    );
    const validEdges = edgesT.filter((edge) => validNodeIds.has(edge.source) && validNodeIds.has(edge.target));
    const { edges: processedEdges } = processEdgesAndHandles(validEdges);

    return {
      nodes: nodesWithBoardHeight,
      edges: processedEdges,
      accumulatedMaxBoardHeight: nextAccumulatedMaxBoardHeight,
    };
  }, [onEditSwimlane, onDeleteSwimlane, isAccessDisabled]);

  return { createNodesAndEdges };
};

export default useCreateNodesAndEdgesValueChain;
